import { DatabaseFieldType, USER_VALUE_CURRENT } from '@databases/types/database';
import { isValid, parse, parseISO } from 'date-fns';
import { z } from 'zod';

import { validateAdf } from '@canvas/adf/validation';

import { stripControlChars } from './strip-control-chars';

export const TEXT_PLAIN_MAX_LENGTH = 100_000;
export const HYPERLINK_URL_MAX_LENGTH = 2_048;

/**
 * Validates the shape of a TEXT field cell value.
 *
 * - `plain` — required string (may be empty). Max length {@link TEXT_PLAIN_MAX_LENGTH}.
 *   C0 control characters (U+0000–U+001F) except LF and CR are stripped.
 * - `rich` — optional ADF document. When provided, it becomes the source of truth
 *   and `plain` is re-derived downstream by `YTextValue.setContent()`.
 *   Validated with `validateAdf` (top-level structure check).
 */
const textValueValidator = z.object({
  plain: z
    .string()
    .transform(stripControlChars)
    .pipe(
      z
        .string()
        .max(
          TEXT_PLAIN_MAX_LENGTH,
          `plain must be at most ${TEXT_PLAIN_MAX_LENGTH} characters`
        )
    ),
  rich: z
    .record(z.unknown())
    .refine((v) => validateAdf(v), {
      message: 'rich must be a valid ADF document',
    })
    .optional(),
});

const emailValidator = z.string().email();

function isAllowedHyperlinkUrl(url: string): boolean {
  let parsedUrl: URL;

  try {
    parsedUrl = new URL(url);
  } catch (e: unknown) {
    return false;
  }

  const { pathname, protocol } = parsedUrl;

  if (protocol === 'http:' || protocol === 'https:') {
    if (parsedUrl.username || parsedUrl.password) {
      return false;
    }

    return true;
  }

  return protocol === 'mailto:' && emailValidator.safeParse(pathname).success;
}

const hyperlinkUrlValidator = z
  .string()
  .max(
    HYPERLINK_URL_MAX_LENGTH,
    `url must be at most ${HYPERLINK_URL_MAX_LENGTH} characters`
  )
  .refine(isAllowedHyperlinkUrl, {
    message: 'Expected http(s) or valid mailto URL',
  });

const hyperlinkItemValidator = z
  .object({
    url: hyperlinkUrlValidator,
    _id: z.string().uuid(),
  })
  .strict();

const contentIdValidator = z.string().regex(/^\d+$/, 'contentId must be numeric');

const contentReferenceValidator = z
  .object({
    contentId: contentIdValidator,
    ari: z.string().optional(),
    _id: z.string().uuid(),
  })
  .strict();

const pageReferenceItemValidator = z.object({
  type: z.literal('page'),
  spaceKey: z.string().min(1),
  contentTitle: z.string().min(1),
  contentId: contentIdValidator.optional(),
  ari: z.string().optional(),
  _id: z.string().uuid(),
  create: z.any().optional(),
});

const canonicalJiraIssueKeyRegex = /^[A-Z0-9]+-[0-9]+$/;

const jiraIssueKeyValidator = z.string().min(1).regex(canonicalJiraIssueKeyRegex);

const jiraIssueItemValidator = z
  .object({
    key: jiraIssueKeyValidator,
    _id: z.string().uuid(),
  })
  .strict();
const jiraIssueValueValidator = z.array(jiraIssueItemValidator);
const pageLinkItemValidator = z.union([
  pageReferenceItemValidator,
  contentReferenceValidator,
]);

const userItemValidator = z
  .object({
    accountId: z.string().min(1),
    _id: z.string().uuid(),
  })
  .strict();

// Default user values are written through the same set-value path as entry
// values, using USER_VALUE_CURRENT as a delayed-resolution sentinel. Concrete
// entry values should use normalized user references instead.

const userValueValidator = z.union([
  z.literal(USER_VALUE_CURRENT),
  z
    .array(userItemValidator)
    .refine(
      (value) => new Set(value.map((user) => user.accountId)).size === value.length,
      {
        message: 'User values must be unique',
      }
    ),
]);

const DATE_VALUE_TODAY = 'today';
const DATE_VALUE_REGEXP = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME_VALUE_REGEXP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?:Z|[+-]\d{2}:\d{2})$/;

function isValidDateValue(value: string): boolean {
  if (value === '' || value === DATE_VALUE_TODAY) {
    return true;
  }

  if (DATE_VALUE_REGEXP.test(value)) {
    return isValid(parse(value, 'yyyy-MM-dd', new Date()));
  }

  if (DATETIME_VALUE_REGEXP.test(value)) {
    return isValid(parseISO(value));
  }

  return false;
}

const NON_DECIMAL_PREFIX = /^[+-]?0[box]/i;

function isValidNumberValue(value: string): boolean {
  if (value === '') {
    return true;
  }

  if (NON_DECIMAL_PREFIX.test(value)) {
    return false;
  }

  return Number.isFinite(Number(value));
}

const dateValueValidator = z.string().refine(isValidDateValue, {
  message: 'Expected "today", YYYY-MM-DD, or YYYY-MM-DDTHH:mmZ / YYYY-MM-DDTHH:mm±HH:mm',
});
const numberValueValidator = z
  .string()
  .transform((v) => v.trim())
  .pipe(
    z.string().refine(isValidNumberValue, {
      message: 'Expected a finite number',
    })
  );
const selectValueValidator = z
  .array(z.string().uuid())
  .refine((value) => new Set(value).size === value.length, {
    message: 'Select values must be unique',
  });
const outOfScopeFieldTypes = new Set<string>([
  DatabaseFieldType.AI_SUMMARY,
  DatabaseFieldType.ATTACHMENT,
  DatabaseFieldType.CHECKBOX,
  DatabaseFieldType.ENTRY_LINK,
]);

/**
 * Per-field-type discriminated union for database cell values. Validates the
 * serializable shape (no `File` objects). Stricter than the loose
 * `databaseValueValidator` in `@databases/types` which uses `value: z.any()`.
 */
const supportedDatabaseValueValidator = z.discriminatedUnion('type', [
  z.object({ type: z.literal(DatabaseFieldType.TEXT), value: textValueValidator }),
  z.object({ type: z.literal(DatabaseFieldType.NUMBER), value: numberValueValidator }),
  z.object({
    type: z.literal(DatabaseFieldType.DATE),
    value: dateValueValidator,
  }),
  z.object({ type: z.literal(DatabaseFieldType.SELECT), value: selectValueValidator }),
  z.object({
    type: z.literal(DatabaseFieldType.HYPERLINK),
    value: z.array(hyperlinkItemValidator),
  }),
  z.object({
    type: z.literal(DatabaseFieldType.PAGE_LINK),
    value: z.array(pageLinkItemValidator),
  }),
  z.object({
    type: z.literal(DatabaseFieldType.USER),
    value: userValueValidator,
  }),
  z.object({
    type: z.literal(DatabaseFieldType.JIRA_ISSUE),
    value: jiraIssueValueValidator,
  }),
]);

export const strictDatabaseValueValidator = z
  .unknown()
  .superRefine((value, ctx) => {
    const valueType =
      typeof value === 'object' && value !== null && 'type' in value
        ? value.type
        : undefined;

    if (typeof valueType === 'string' && outOfScopeFieldTypes.has(valueType)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['type'],
        message: `Field type '${valueType}' is not supported by the SDK yet`,
      });
    }
  })
  .pipe(supportedDatabaseValueValidator);

export type StrictDatabaseValue = z.infer<typeof strictDatabaseValueValidator>;

export const databaseEntryValidator = z.object({
  id: z.string().min(1),
  values: z.record(z.string(), strictDatabaseValueValidator),
});
